import { t as translate } from "./preferences";
import { warehouseValidationMessage } from "./warehouseErrors";
export type Role = "platform_owner" | "workshop_owner" | "employee" | "client";

export type UserOut = {
  id: number;
  full_name: string;
  phone: string;
  role: Role;
  workshop_id: number | null;
  branch_id: number | null;
  branch_name?: string | null;
  must_change_password: boolean;
  workshop_expired: boolean;
  can_be_usta: boolean;
  can_be_sotuvchi: boolean;
  can_be_omborchi: boolean;
};

export type TokenOut = {
  access_token: string;
  token_type: string;
  user: UserOut;
};

export type WorkSummary = {
  master_name?: string;
  mechanics?: { user_id: number; full_name: string }[];
  next_service_km?: number | null;
  due_at?: string | null;
  id: number;
  service_id: number;
  service_name: string;
  price: string;
  parts_total: string;
  status?: "active" | "cancelled";
  km: number | null;
  note: string | null;
  created_at: string;
  plate_number?: string | null;
  client_name?: string | null;
};

export type WorkPart = {
  movement_id: number; product_id: number; product_nomi: string;
  miqdor: string; birlik_narx: string; jami_summa: string;
};

export type CarHistoryItem = {
  work_id: number;
  created_at: string;
  service_name: string;
  km: number | null;
  price: string;
  master_name: string;
  note?: string | null;
  next_service_km?: number | null;
  due_at?: string | null;
};

export type CarWithHistory = {
  id: number;
  plate_number: string;
  model: string | null;
  year: number | null;
  km: number;
  next_due_at: string | null;
  history: CarHistoryItem[];
};

export type ClientDetails = {
  id: number;
  full_name: string;
  phone: string;
  telegram_id: number | null;
  cars: CarWithHistory[];
};

export type ClientOut = {
  id: number;
  workshop_id: number;
  full_name: string;
  phone: string;
  telegram_id: number | null;
};

export type CarOut = {
  id: number;
  workshop_id: number;
  client_id: number;
  plate_number: string;
  model: string | null;
  year: number | null;
  km: number;
};

export type CarSearchItem = {
  car_id: number;
  plate_number: string;
  model: string | null;
  year: number | null;
  km: number;
  client_id: number;
  client_full_name: string;
  client_phone: string;
  last_work_at: string | null;
};

export type MechanicStats = {
  period: "day" | "week" | "month" | "quarter";
  total_works: number;
  total_revenue: number;
  by_service: { service_name: string; count: number; revenue: number }[];
  telegram_links: { linked: number; not_linked: number };
};

export type MiniCarDetail = {
  car: CarOut;
  client: ClientOut;
  recent_works: WorkSummary[];
};

export type ServiceOut = {
  id: number;
  name: string;
  narx: string | number | null;
  sms_text?: string | null;
  is_active: boolean;
};

export type ProductOut = {
  id: number;
  workshop_id?: number;
  branch_id?: number | null;
  nomi: string;
  artikul?: string | null;
  kategoriya?: string | null;
  birlik: string;
  barcode?: string | null;
  tannarx?: string | number | null;
  sotuv_narx: string | number;
  joriy_ostatka: string | number;
  min_ostatka?: string | number;
  faol: boolean;
  holat?: string;
};

export type SupplierOut = {
  id: number;
  workshop_id: number;
  name: string;
  phone: string | null;
  contact_person?: string | null;
  faol: boolean;
};

export type PageOut<T> = { items: T[]; total: number; page: number; page_size: number };
export type WarehouseWarningOut = { id: number; ogohlantirishlar?: string[] };
export type JarayondaItemOut = { product_id: number; product_nomi: string; quantity: string | number };
export type JarayondaSaleOut = {
  id: number;
  client_id?: number | null;
  client_name?: string | null;
  workshop_id: number;
  branch_id: number | null;
  status: "jarayonda" | "active" | "cancelled";
  izoh: string | null;
  created_at: string;
  created_by_user_id: number | null;
  created_by_name: string | null;
  items: JarayondaItemOut[];
  jami_summa: string | number | null;
};

export type WarehouseOperationKind =
  | "purchase"
  | "sale"
  | "purchase-return"
  | "sale-return"
  | "purchase-document-return"
  | "sale-document-return"
  | "write-off"
  | "jarayonda-reserve"
  | "jarayonda-confirm";

export type TariffStatusOut = {
  tariff: string;
  status: string;
  is_expired: boolean;
  features: string[];
};

export type PaymentMethod = "naqd" | "terminal" | "karta_otkazma" | "bank";

export type ReturnType = "purchase_return" | "sale_return";
export type Page<T> = { items: T[]; total: number; page: number; page_size: number };
export type ReturnSource = {
  original_document_id: number; return_type: ReturnType; created_at: string;
  branch_id: number | null; counterparty_name: string | null; original_total: string;
  return_state: "no_returns" | "partially_returned" | "fully_returned";
  can_return: boolean; unavailable_reason: string | null;
};
export type ReturnableItem = {
  product_id: number; product_nomi: string; birlik: string;
  original_quantity: string; returned_quantity: string; unit_price: string;
  original_value: string; returned_value: string; valuation_warning?: string | null;
  stock_quantity: string; reserved_quantity: string; returnable_max: string;
  selected_quantity: string; return_amount: string;
};
export type ReturnProjection = ReturnSource & {
  items: ReturnableItem[]; counterparty_balance: string; debt_reduction_max: string;
  return_value: string; suggested_debt_reduction: string; suggested_cash_refund: string;
  preview_token: string;
};
export type ReturnSelection = {
  return_type: ReturnType; original_document_id: number; return_all: boolean;
  items?: { product_id: number; quantity: string }[];
};
export type ReturnResult = WarehouseWarningOut & {
  return_value: string; debt_reduction: string | null; cash_refund: string | null;
  payments: { tolov_usuli: PaymentMethod; summa: string }[]; money_event_id: number | null;
};

export type WorkCreateInput = {
  car_id: number;
  service_id: number;
  services?: { service_id: number; price: string; reminder_text_override?: string | null }[];
  user_id: number;
  price: string;
  note: string | null;
  reminder_note: string | null;
  reminder_text_override?: string | null;
  parts: { product_id: number; miqdor: string; sotuv_narx: string }[];
  client_uuid: string;
  tolangan_summa?: string | null;
  debt_due_days?: number;
  tolov_usuli?: PaymentMethod;
  payments?: { tolov_usuli: PaymentMethod; summa: string }[];
};

export type ClientOverview = {
  id: number;
  full_name: string;
  phone: string;
  telegram_id: number | null;
  plates: string[];
  visits: number;
  balans?: string | null;
};

export type WorkCreateResult = {
  id: number;
  car_id: number;
  service_id: number;
  user_id: number;
  price: string;
  parts_total: string;
  tolangan_summa: string;
  tolov_holati: string;
  tolov_usuli: PaymentMethod | null;
  payments?: { tolov_usuli: PaymentMethod | null; summa: string }[];
  note: string | null;
  created_at: string;
  client: ClientOut | null;
  reminder: {
    id: number;
    due_at: string;
    note: string | null;
    channel: "telegram" | "sms";
    status: string;
  } | null;
  services?: { service_id: number; name: string; price: string }[];
};

export type WorkshopInviteLink = { token: string; deep_link: string };

export type ReminderLogRow = {
  id: number;
  created_at: string;
  due_at: string | null;
  sent_at: string | null;
  channel: "sms" | "telegram";
  status: string;
  client_id: number;
  client_name: string;
  client_telegram_id: number | null;
  text: string | null;
  source: "reminder" | "sms_log";
};

export type ReminderSendResult = {
  reminder_id: number;
  status: "sent";
  message: string;
};

export type SupportTicket = {
  id: number; type: "support" | "feedback"; title: string; body: string;
  status: "new" | "seen" | "replied"; admin_reply: string | null;
  replied_at: string | null; has_unread_reply: boolean; created_at: string;
};
export type MessageInput = { client_id: number; text: string; send_uuid: string; template_key?: string };
export type MessageResult = { log_id: number | null; status: string; blocked: boolean };

type AccessObserver = (error: ApiError) => void;
let accessObserver: AccessObserver | null = null;
let writeGuard: (() => boolean) | null = null;
export function observeAccess(observer: AccessObserver, guard: () => boolean) {
  accessObserver = observer; writeGuard = guard;
  return () => { if (accessObserver === observer) { accessObserver = null; writeGuard = null; } };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(code: string, message: string, status: number, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export const TOKEN_KEY = "avtodaftar_mechanic_access_token";

export function buildApiBaseUrl(configured: string | undefined): string {
  return `${(configured ?? "").trim().replace(/\/+$/, "")}/api/v1`;
}

export const API_BASE_URL = buildApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

let token: string | null = null;

export function getToken(): string | null {
  if (token) return token;
  token = window.localStorage.getItem(TOKEN_KEY);
  return token;
}

export function setToken(next: string | null): void {
  token = next;
  if (next) window.localStorage.setItem(TOKEN_KEY, next);
  else window.localStorage.removeItem(TOKEN_KEY);
}

async function apiError(response: Response, warehouseValidation = false): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string; [key: string]: unknown }; detail?: unknown } = {};
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // An empty/non-JSON response still becomes a predictable ApiError.
  }
  const code = body.error?.code ?? `http_${response.status}`;
  const specific = warehouseValidation ? warehouseValidationMessage(code, body.error?.message, body.detail)
    ?? body.error?.message ?? (response.statusText || translate("So‘rov bajarilmadi")) : undefined;
  return new ApiError(
    code, specific ?? body.error?.message ?? response.statusText ?? translate("So‘rov bajarilmadi"),
    response.status,
    warehouseValidation && body.detail !== undefined ? { ...body.error, validation: body.detail } : body.error ?? {},
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (init.method && init.method !== "GET" && !path.startsWith("/auth/") && writeGuard && !writeGuard()) {
    throw new ApiError("session_revalidation_required", translate("Sessiyani tekshiring va qayta urinib ko‘ring"), 403);
  }
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const currentToken = getToken();
  if (currentToken) headers.set("Authorization", `Bearer ${currentToken}`);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { cache: "no-store", ...init, headers });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("request_timeout", translate("Server javobi kutilmoqda. Natijani tekshiramiz."), 408);
    }
    throw new ApiError(
      "network_error",
      translate("Internet aloqasini tekshirib, qayta urinib ko‘ring"),
      0,
    );
  }

  if (!response.ok) {
    const error = await apiError(response, init.method === "POST" && ["/sales", "/purchases", "/returns", "/returns/preview"].includes(path));
    if (currentToken === getToken() && path !== "/auth/login" && [401, 402, 403].includes(error.status)) accessObserver?.(error);
    throw error;
  }
  if (currentToken !== getToken() && path !== "/auth/login") throw new ApiError("session_changed", translate("Sessiya o‘zgardi"), 401);
  if (response.status === 204) return undefined as T;
  try { return (await response.json()) as T; }
  catch { throw new ApiError("invalid_response", translate("So‘rov bajarilmadi"), 502); }
}

export const api = {
  sendMessage: (payload: MessageInput, signal?: AbortSignal) => request<MessageResult>("/messaging/send", { method: "POST", body: JSON.stringify(payload), signal }),
  tickets: () => request<SupportTicket[]>("/support/tickets/my"),
  createTicket: (payload: { type: "support" | "feedback"; title: string; body: string }, signal?: AbortSignal) => request<{ id: number; status: string }>("/support/tickets", { method: "POST", body: JSON.stringify(payload), signal }),
  acknowledgeReplies: (replies: { id: number; replied_at: string }[]) => request<{ acknowledged: number }>("/support/tickets/my/read", { method: "PATCH", body: JSON.stringify({ replies }) }),
  login: (identifier: string, password: string) =>
    request<TokenOut>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ identifier, password }),
    }),

  me: (signal?: AbortSignal) => request<UserOut>("/auth/me", { signal }),

  updateProfile: (payload: { full_name: string; phone: string }) =>
    request<UserOut>("/auth/me", { method: "PATCH", body: JSON.stringify({ ...payload, phone: payload.phone.replace(/\D/g, "") ? `+${payload.phone.replace(/\D/g, "")}` : payload.phone.trim() }) }),

  changePassword: (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ) =>
    request<UserOut>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    }),

  async logout(): Promise<void> {
    const headers = new Headers();
    const currentToken = getToken();
    if (currentToken) headers.set("Authorization", `Bearer ${currentToken}`);

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        headers,
      });
    } catch {
      throw new ApiError(
        "network_error",
        translate("Chiqishni tasdiqlab bo‘lmadi. Qayta urinib ko‘ring"),
        0,
      );
    }

    // A 401 confirms that the local credential is already unusable.
    if (response.status === 401) return;
    if (!response.ok) throw await apiError(response);
    const body = (await response.json()) as { ok?: boolean };
    if (body.ok !== true) {
      throw new ApiError("logout_failed", translate("Chiqishni tasdiqlab bo‘lmadi"), 500);
    }
  },

  mechanicStats: (period: MechanicStats["period"]) =>
    request<MechanicStats>(`/mechanic/stats?period=${period}`),

  myWorks: (limit = 20, offset = 0) =>
    request<WorkSummary[]>(`/miniapp/my-works?limit=${limit}&offset=${offset}`),

  workDetails: (id: number) => request<{ id: number; parts: WorkPart[] }>(`/works/${id}`),

  ownerReminders: (pageSize = 50) =>
    request<PageOut<ReminderLogRow>>(`/owner/reminders?page_size=${pageSize}`),
  myReminders: (pageSize = 50) =>
    request<PageOut<ReminderLogRow>>(`/miniapp/my-reminders?page_size=${pageSize}`),
  sendOwnerReminder: (id: number) =>
    request<ReminderSendResult>(`/owner/reminders/${id}/send`, { method: "POST" }),
  sendMyReminder: (id: number) =>
    request<ReminderSendResult>(`/miniapp/my-reminders/${id}/send`, { method: "POST" }),

  search: (query: string) =>
    request<{ query_type: "phone" | "plate"; items: CarSearchItem[] }>(
      `/miniapp/search?q=${encodeURIComponent(query)}`,
    ),

  clients: () => request<ClientDetails[]>("/miniapp/clients?limit=200"),
  clientsOverview: (page = 1, query = "") =>
    request<PageOut<ClientOverview>>(`/miniapp/clients/overview?page=${page}&page_size=10${query ? `&q=${encodeURIComponent(query)}` : ""}`),
  client: (id: number) => request<ClientDetails>(`/miniapp/clients/${id}`),
  car: (id: number) => request<MiniCarDetail>(`/miniapp/cars/${id}`),
  services: () => request<ServiceOut[]>("/services?include_inactive=false"),
  products: () => request<ProductOut[]>("/products?faol=true"),
  productByBarcode: (barcode: string) =>
    request<ProductOut>(`/products/barcode/${encodeURIComponent(barcode)}`),
  suppliers: () =>
    request<PageOut<SupplierOut>>("/suppliers?faol=true&page=1&page_size=200"),
  async tariff(signal?: AbortSignal): Promise<TariffStatusOut> {
    const result = await request<TariffStatusOut>("/owner/tariff", { signal });
    if (!Array.isArray(result.features)) throw new ApiError("invalid_response", translate("So‘rov bajarilmadi"), 502);
    return result;
  },
  createWork: (payload: WorkCreateInput, signal?: AbortSignal) =>
    request<WorkCreateResult>("/works", {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    }),
  workshopInviteLink: () => request<WorkshopInviteLink>("/owner/workshop-invite-link"),

  createPurchase: (payload: unknown, signal?: AbortSignal) => request<WarehouseWarningOut>("/purchases", {
    method: "POST", body: JSON.stringify(payload), signal,
  }),
  createSale: (payload: unknown, signal?: AbortSignal) => request<WarehouseWarningOut>("/sales", {
    method: "POST", body: JSON.stringify(payload), signal,
  }),
  createPurchaseReturn: (payload: unknown, signal?: AbortSignal) => request<WarehouseWarningOut>("/mobile/returns/purchase-return", {
    method: "POST", body: JSON.stringify(payload), signal,
  }),
  returnSources: (returnType: ReturnType, page = 1) => request<Page<ReturnSource>>(`/returns/sources?return_type=${returnType}&page=${page}&page_size=20`),
  returnability: (returnType: ReturnType, documentId: number) => request<ReturnProjection>(`/returns/returnability?return_type=${returnType}&original_document_id=${documentId}`),
  previewReturn: (payload: ReturnSelection) => request<ReturnProjection>("/returns/preview", { method: "POST", body: JSON.stringify(payload) }),
  createLinkedReturn: (payload: unknown, signal?: AbortSignal) => request<ReturnResult>("/returns", { method: "POST", body: JSON.stringify(payload), signal }),
  createSaleReturn: (payload: unknown, signal?: AbortSignal) => request<WarehouseWarningOut>("/mobile/returns/sale-return", {
    method: "POST", body: JSON.stringify(payload), signal,
  }),
  createWriteOff: (payload: unknown, signal?: AbortSignal) => request<WarehouseWarningOut>("/write-offs", {
    method: "POST", body: JSON.stringify(payload), signal,
  }),
  jarayonda: () => request<JarayondaSaleOut[]>("/mobile/sales/jarayonda"),
  createJarayonda: (payload: unknown, signal?: AbortSignal) => request<WarehouseWarningOut>("/mobile/sales/jarayonda", {
    method: "POST", body: JSON.stringify(payload), signal,
  }),
  jarayondaState: (id: number) =>
    request<{ id: number; status: JarayondaSaleOut["status"] }>(`/mobile/sales/${id}/jarayonda-state`),
  confirmJarayonda: (id: number, payload: unknown, signal?: AbortSignal) =>
    request<WarehouseWarningOut>(`/mobile/sales/${id}/tasdiqlash`, {
      method: "PATCH", body: JSON.stringify(payload), signal,
    }),

  createClient: (payload: { full_name: string; phone: string; client_uuid: string }) =>
    request<ClientOut>("/clients", { method: "POST", body: JSON.stringify(payload) }),
  updateClient: (id: number, payload: { full_name: string; phone: string }) =>
    request<ClientOut>(`/clients/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  createCar: (payload: {
    client_id: number; plate_number: string; model: string | null;
    year?: number | null; km: number; client_uuid: string;
  }) => request<CarOut>("/cars", { method: "POST", body: JSON.stringify(payload) }),
  updateCar: (id: number, payload: {
    plate_number: string; model: string | null; year?: number | null; km: number;
  }) => request<CarOut>(`/cars/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
};
